import moment from 'moment/moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { BQLink } from '../../../../atoms/bqlink';
import Loader from '../../../../atoms/icons/loader';
import { ArrowRightIcon } from '../../../../atoms/material-icons';
import EmptyState from '../empty-state';
import { isWithinTradingHours, widgetLink } from '../../../../../helpers/utils';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import './accumulation-table-futures.m.css';

const AccumulationTableFutures = ({ limit, external, widget = false }) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [tableData, setTableData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('accumulation');
  const [loading, setLoading] = useState(false);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  const getTableData = async (filterValue = 'accumulation') => {
    let price, oi, order;
    if (filterValue === 'accumulation') {
      price = 1;
      oi = 1;
      order = 1;
    } else if (filterValue === 'liquidation') {
      price = 0;
      oi = 0;
      order = 0;
    } else if (filterValue === 'short-covering') {
      price = 1;
      oi = 0;
      order = 0;
    } else if (filterValue === 'fresh-shorts') {
      price = 0;
      oi = 1;
      order = 1;
    }

    const response = await fetch(
      `/foapi/future/getByhedging?price=${price}&oi=${oi}&order=${order}&limit=${limit}`,
    );
    return response.json();
  };

  const resultTableData = useQuery('getTableData', () => getTableData(selectedFilter), {
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      setTableData(data?.data);
    },
    enabled:
      initialLoading ||
      (!!selectedFilter && isWithinTradingHours(tradingHolidays, specialTradingDays)),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (resultTableData?.isSuccess) {
      setTableData(resultTableData?.data?.data);
    }
  }, []);

  const filterButtonHandler = async (filterValue) => {
    setLoading(true);
    setSelectedFilter(filterValue);
    const data = await getTableData(filterValue);
    setTableData(data?.data);
    setLoading(false);
  };

  return (
    <React.Fragment>
      {initialLoading ? (
        <div styleName="initial-loading-container"></div>
      ) : (
        <div styleName={`overall-table-container ${external ? 'widget-mode' : ''}`}>
          <div styleName="btn-container">
            <button
              styleName={`filter-btn ${selectedFilter === 'accumulation' ? 'selected' : ''}`}
              onClick={() => filterButtonHandler('accumulation')}
            >
              Accumulation
            </button>
            <button
              styleName={`filter-btn ${selectedFilter === 'liquidation' ? 'selected' : ''}`}
              onClick={() => filterButtonHandler('liquidation')}
            >
              Liquidation
            </button>
            <button
              styleName={`filter-btn ${selectedFilter === 'short-covering' ? 'selected' : ''}`}
              onClick={() => filterButtonHandler('short-covering')}
            >
              Short Covering
            </button>
            <button
              styleName={`filter-btn ${selectedFilter === 'fresh-shorts' ? 'selected' : ''}`}
              onClick={() => filterButtonHandler('fresh-shorts')}
            >
              Fresh Shorts
            </button>
          </div>
          {Array.isArray(tableData) && tableData?.length !== 0 ? (
            <>
              {loading ? (
                <div styleName="loading-container-ext">
                  <div styleName="loading-container-int">
                    <Loader color={'#dadee1'} />
                  </div>
                </div>
              ) : (
                <div styleName="content-container">
                  <div styleName="table-body-container">
                    <table>
                      <thead styleName="table-header">
                        <tr>
                          <th styleName="column-title">Security</th>
                          <th styleName="column-title-alt">Expiry</th>
                          <th styleName="column-title-alt">
                            Price
                            <br />
                            (Futures)
                          </th>
                          <th styleName="column-title-alt">Change</th>
                          <th styleName="column-title-alt">% Change</th>
                          <th styleName="column-title-alt">Basis</th>
                          <th styleName="column-title-alt">
                            Open Interest
                            <br /> (In Contracts)
                          </th>
                          <th styleName="column-title-alt">
                            Open Interest
                            <br /> (% Change)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(tableData) &&
                          tableData?.map((dataPoint, index) => (
                            <tr styleName="data-row" key={index}>
                              <td>
                                {dataPoint?.symbol ? (
                                  <BQLink
                                    href={widgetLink(
                                      `/markets/futures-contract/${dataPoint?.symbol}${widgetParam}`,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span styleName="name">{dataPoint?.symbol}</span>
                                  </BQLink>
                                ) : (
                                  <span styleName="name">-</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {moment(dataPoint['expiry-date']).format('DD MMM')}
                              </td>
                              <td styleName="column-value">
                                {dataPoint.cmp ? dataPoint.cmp : '-'}
                              </td>
                              <td styleName="column-value">
                                {dataPoint['future-change'] ? (
                                  <span>
                                    {String(dataPoint['future-change']).includes('-') ? (
                                      <span styleName="negative-value">
                                        {`${dataPoint['future-change']?.toFixed(2)}`}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['future-change']?.toFixed(2)}`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint['cmp-change-percentage'] ? (
                                  <span>
                                    {String(dataPoint['cmp-change-percentage']).includes('-') ? (
                                      <span styleName="negative-value">
                                        {`${dataPoint['cmp-change-percentage']?.toFixed(2)}%`}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['cmp-change-percentage']?.toFixed(2)}%`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint['premium-discount'] ? (
                                  <span>
                                    {String(dataPoint['premium-discount']).includes('-') ? (
                                      <span styleName="negative-value">
                                        {`${dataPoint['premium-discount']?.toFixed(2)}`}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['premium-discount']?.toFixed(2)}`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                              <td styleName="column-value">
                                {dataPoint['open-interest'] ? dataPoint['open-interest'] : '-'}
                              </td>
                              <td styleName="column-value">
                                {dataPoint['open-interest-change-percentage'] ? (
                                  <span>
                                    {String(dataPoint['open-interest-change-percentage']).includes(
                                      '-',
                                    ) ? (
                                      <span styleName="negative-value">
                                        {`${dataPoint['open-interest-change-percentage']?.toFixed(2)}%`}
                                      </span>
                                    ) : (
                                      <span styleName="positive-value">
                                        {`+${dataPoint['open-interest-change-percentage']?.toFixed(2)}%`}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span>0.00</span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState />
              </div>
            </div>
          )}

          {external === false && (
            <div>
              <BQLink
                styleName="view-more-btn"
                href={widgetLink(`/markets/futures-contract/short-covering${widgetParam}`, widget)}
                target="_blank"
                rel="noreferrer"
              >
                View All Futures
                <ArrowRightIcon styleName="tab-icon" color={'#2b2b2b'} />
              </BQLink>
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

AccumulationTableFutures.propTypes = {
  limit: PropTypes.number,
  external: PropTypes.bool,
};

AccumulationTableFutures.defaultProps = {
  limit: 10,
  external: false,
};

export default AccumulationTableFutures;
